import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../utils";

const badgeVariants = cva(
  "al-inline-flex al-items-center al-rounded-full al-border al-px-2.5 al-py-0.5 al-text-xs al-font-semibold al-transition-colors focus:al-outline-none focus:al-ring-2 focus:al-ring-ring focus:al-ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "al-border-transparent al-bg-primary al-bg-bg-brand al-text-primary-foreground hover:al-bg-primary/80",
        secondary:
          "al-border-transparent al-bg-secondary al-text-secondary-foreground hover:al-bg-secondary/80",
        destructive:
          "al-border-transparent al-bg-destructive al-text-destructive-foreground hover:al-bg-destructive/80",
        success: "al-border-transparent al-bg-green-100 al-text-green-500 hover:al-bg-green-600",
        outline: "al-text-foreground",
        warning: "al-border-transparent al-bg-[#ffeae4] al-text-[#ff7064] hover:al-bg-bg-orange/80",
      },
      interactive: {
        true: "al-cursor-pointer focus:al-ring-2 focus:al-ring-ring focus:al-ring-offset-2",
        false: "al-cursor-default",
      },
    },
    defaultVariants: {
      variant: "default",
      interactive: false,
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  /**
   * If true, the badge will be focusable and handle keyboard events
   */
  interactive?: boolean;
  /**
   * Optional click handler for interactive badges
   */
  onAction?: () => void;
}

function Badge({
  className,
  variant,
  interactive = true,
  onAction,
  children,
  role = "status",
  tabIndex,
  onKeyDown,
  ...props
}: BadgeProps) {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (interactive && onAction && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      onAction();
    }
    onKeyDown?.(e);
  };

  return (
    <div
      className={cn(badgeVariants({ variant, interactive }), className)}
      role={interactive ? "button" : role}
      tabIndex={interactive ? 0 : tabIndex}
      onKeyDown={handleKeyDown}
      onClick={interactive ? onAction : undefined}
      aria-disabled={interactive && !onAction}
      {...props}
    >
      {children}
    </div>
  );
}

export { Badge, badgeVariants };
